'use client'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  LinkButton,
} from '@mdxui/primitives'
import { cn } from '../../lib/cn'

export interface TeamCardsMember {
  name: string
  role: string
  department?: string
  description?: string
  expertise?: string[]
  avatar?: string
  href?: string
}

export interface TeamCardsProps {
  title?: string
  description?: string
  /** Optional CTA button */
  cta?: {
    text: string
    url: string
  }
  members?: TeamCardsMember[]
  /** Show department badge under role */
  showBadge?: boolean
  /** Show member description */
  showDescription?: boolean
  /** Show expertise tags at bottom */
  showBottomBadges?: boolean
  /** Show learn more button */
  showButton?: boolean
  className?: string
}

const defaultMembers: TeamCardsMember[] = [
  {
    name: 'Priya',
    role: 'Product Manager',
    department: 'Leadership',
    description:
      'Drives product strategy and roadmaps with a focus on user-centric development. Passionate about turning complex problems into elegant solutions.',
    expertise: ['Product Strategy', 'Roadmaps', 'User Research'],
    avatar: '/avatars/Priya.png',
    href: '/team/priya',
  },
  {
    name: 'Tom',
    role: 'Software Engineer',
    department: 'Engineering',
    description:
      'Full-stack developer focused on building reliable, well-tested applications. Expert in turning designs into production-ready code.',
    expertise: ['Full-Stack', 'Testing', 'Code Quality'],
    avatar: '/avatars/Tom.png',
    href: '/team/tom',
  },
  {
    name: 'Sam',
    role: 'Security Engineer',
    department: 'Engineering',
    description:
      'Conducts security audits and penetration testing to keep systems safe. Passionate about building secure-by-default infrastructure.',
    expertise: ['Security Audits', 'Penetration Testing', 'Compliance'],
    avatar: '/avatars/Sam.png',
    href: '/team/sam',
  },
  {
    name: 'Ralph',
    role: 'Technical Architect',
    department: 'Engineering',
    description:
      'Designs scalable system architectures and leads technical decisions. Expert in building infrastructure that grows with the product.',
    expertise: ['System Design', 'Architecture', 'Scalability'],
    avatar: '/avatars/Ralph.png',
    href: '/team/ralph',
  },
  {
    name: 'Archie',
    role: 'DevOps Engineer',
    department: 'Engineering',
    description:
      'Builds and maintains CI/CD pipelines and cloud infrastructure. Focused on reliability, automation, and developer experience.',
    expertise: ['CI/CD', 'Cloud Infrastructure', 'Automation'],
    avatar: '/avatars/Archie.png',
    href: '/team/archie',
  },
  {
    name: 'Luna',
    role: 'UX Designer',
    department: 'Design',
    description:
      'Creates intuitive user experiences through research and wireframes. Believes great design should be invisible and solve real user problems.',
    expertise: ['User Research', 'Wireframes', 'Prototyping'],
    href: '/team/luna',
  },
]

/**
 * TeamCards - Card-based team grid with detailed member profiles
 *
 * A team section with centered header and 3-column card grid.
 * Each card shows avatar, name, role, department badge, description, and expertise tags.
 *
 * Best for: Leadership teams, about pages, smaller teams (3-9 members)
 */
export function TeamCards({
  title = 'Team',
  description = 'Our diverse team of experts brings together decades of experience in design, engineering, and product development.',
  cta,
  members = defaultMembers,
  showBadge = true,
  showDescription = true,
  showBottomBadges = true,
  showButton = true,
  className,
}: TeamCardsProps) {
  return (
    <section className={cn('bg-gradient-to-b from-background to-muted/20 py-32', className)}>
      <div className="container mx-auto">
        {/* Header Section */}
        <div className="mb-16 text-center">
          <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">{title}</h2>
          <p className="mx-auto mt-4 max-w-3xl text-muted-foreground">
            {description}
          </p>
          {cta && (
            <Button size="lg" className="mt-8 w-full sm:w-auto" asChild>
              <a href={cta.url}>{cta.text}</a>
            </Button>
          )}
        </div>

        {/* Team Grid */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {members.map((member) => (
            <Card
              key={member.name}
              className="group border-0 bg-card/50 backdrop-blur-sm transition-all duration-300 hover:shadow-lg"
            >
              <CardContent className="flex h-full flex-col px-6 pt-6 pb-4">
                {/* Avatar */}
                <div className="relative mb-6">
                  <Avatar className="mx-auto h-20 w-20">
                    <AvatarImage src={member.avatar} alt={member.name} />
                    <AvatarFallback className="bg-gradient-to-br from-primary/20 to-secondary/20 text-2xl font-semibold text-primary">
                      {member.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                </div>

                {/* Member Info */}
                <div className="mb-4 text-center">
                  <h3 className="mb-1 text-lg font-semibold">
                    {member.name}
                  </h3>
                  <p className="mb-2 text-sm font-medium text-primary">{member.role}</p>
                  {showBadge && member.department && (
                    <Badge variant="outline" className="text-xs">
                      {member.department}
                    </Badge>
                  )}
                </div>

                {/* Description */}
                {showDescription && member.description && (
                  <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
                    {member.description}
                  </p>
                )}

                {/* Expertise Tags */}
                {showBottomBadges && member.expertise && member.expertise.length > 0 && (
                  <div className="mt-4 flex flex-wrap justify-center gap-1">
                    {member.expertise.map((skill, index) => (
                      <Badge key={index.toString()} variant="secondary" className="bg-muted/50 px-2 py-1 text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Learn More Link */}
                {showButton && member.href && (
                  <div className="mt-4 flex justify-center">
                    <LinkButton href={member.href}>Learn more</LinkButton>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}
